import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@material-ui/core';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData, postData } from '../../../apiService';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CopyrightIcon from '@mui/icons-material/Copyright';
import './InvoiceGenerate.css';
import { generateInvoice } from './InvoicePrint';

const InvoiceGenerate = () => {
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [invoiceData, setInvoiceData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [existingInvoice, setExistingInvoice] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [invoiceChildDetails, setInvoiceChildDetails] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [modalExpandedCategories, setModalExpandedCategories] = useState({});
    const { isAuthenticated, companyId, token, logout, userId } = useAuth();
    const history = useHistory();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const today = new Date().toISOString().split('T')[0];
        setFromDate(today);
        setToDate(today);
        fetchCustomerOptions();
    }, [isAuthenticated, token, logout]);

    const fetchCustomerOptions = async () => {
        try {
            const response = await fetchData('CustomerApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                const data = response.response_data;
                if (Array.isArray(data)) {
                    const options = data.map(item => ({
                        value: item.Id,
                        label: `${item.Name}-${item.ContactNumber1 || ''}`,
                    }));
                    setCustomerOptions(options);
                }
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomerOptions([]);
        }
    };

    const handleLoadData = async () => {
        if (!selectedCustomer) {
            setErrorMessage('Please select a customer');
            return;
        }
        if (!fromDate || !toDate) {
            setErrorMessage('Please select date range');
            return;
        }

        setErrorMessage('');
        setIsLoading(true);
        setExistingInvoice(null);
        setInvoiceData([]);

        try {
            // First, check if invoice already exists for this period
            const checkResponse = await fetchData(
                'InvoiceApi',
                `/Invoice/${companyId}/${selectedCustomer.value}/${fromDate}/${toDate}`,
                token
            );

            if (checkResponse.responseCode === '1111' && checkResponse.response_data) {
                const invoiceExists = checkResponse.response_data;
                if (invoiceExists.IsGenerated || invoiceExists.BillNumber) {
                    // Invoice already exists
                    setExistingInvoice(invoiceExists);
                    setIsLoading(false);
                    return;
                }
            }

            // If no existing invoice, load the data
            const response = await fetchData(
                'InvoiceApi',
                `/FoodRegister/${companyId}/${selectedCustomer.value}/${fromDate}/${toDate}`,
                token
            );

            if (response.responseCode === '1111') {
                const data = response.response_data;
                if (Array.isArray(data) && data.length > 0) {
                    // Calculate Amount = Quantity * Rate for each item
                    const calculatedData = data.map(item => ({
                        ...item,
                        Amount: (item.Quantity || 0) * (item.Rate || 0)
                    }));
                    setInvoiceData(calculatedData);
                } else {
                    setInvoiceData([]);
                    setErrorMessage('No data found for the selected criteria');
                }
            } else {
                setInvoiceData([]);
                setErrorMessage('Failed to load invoice data');
            }
        } catch (error) {
            console.error('Error loading invoice data:', error);
            setInvoiceData([]);
            setErrorMessage('Error loading invoice data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewInvoiceDetails = async () => {
        if (!existingInvoice || !existingInvoice.BillNumber) return;

        setIsLoading(true);
        try {
            const response = await fetchData(
                'InvoiceApi',
                `/${existingInvoice.Id}`,
                token
            );

            if (response.responseCode === '1111') {
                setInvoiceDetails(response.response_data.Invoice);
                // Calculate Amount = Rate * Quantity for each item
                const calculatedData = response.response_data.DailyFoodRegister.map(item => ({
                    ...item,
                    Amount: (item.Quantity || 0) * (item.Rate || 0)
                }));
                setInvoiceChildDetails(calculatedData);
                setShowInvoiceModal(true);
            } else {
                setErrorMessage('Failed to load invoice details');
            }
        } catch (error) {
            console.error('Error loading invoice details:', error);
            setErrorMessage('Error loading invoice details');
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        setShowInvoiceModal(false);
        setInvoiceDetails(null);
        setInvoiceChildDetails(null);
    };

    const handleCreateInvoice = async () => {
        if (!selectedCustomer || invoiceData.length === 0) {
            setErrorMessage('No data available to create invoice');
            return;
        }

        // First, check if invoice already exists for this period
        const checkResponse = await fetchData(
            'InvoiceApi',
            `/Invoice/${companyId}/${selectedCustomer.value}/${fromDate}/${toDate}`,
            token
        );

        if (checkResponse.responseCode === '1111' && checkResponse.response_data) {
            const invoiceExists = checkResponse.response_data;
            if (invoiceExists.IsGenerated || invoiceExists.BillNumber) {
                // Invoice already exists
                setExistingInvoice(invoiceExists);
                setIsLoading(false);
                return;
            }
        }


        setIsLoading(true);
        setErrorMessage('');

        try {
            const currentDate = new Date().toISOString().split('T')[0];

            const invoicePayload = {
                CompanyId: companyId,
                CustomerId: selectedCustomer.value,
                InvoiceFromDate: fromDate,
                InvoiceToDate: toDate,
                BillDate: currentDate,
                TotalQuantity: getTotalQuantity(),
                Amount: getSubTotal(),
                TransportationCharges: getTotalTransportation(),
                TotalAmount: getGrandTotal(),
                CreatedBy: userId
            };

            const response = await postData('InvoiceApi', invoicePayload, token);

            if (response.responseCode === "1111") {
                setShowSuccessPopup(true);
                setTimeout(async () => {
                    setShowSuccessPopup(false);
                    // Reset form
                    setInvoiceData([]);
                    setSelectedCustomer(null);
                    const today = new Date().toISOString().split('T')[0];
                    setFromDate(today);
                    setToDate(today);
                    await generateInvoice(response.kotId, token);
                }, 2000);
            } else {
                setErrorMessage(response.responseMessage || 'Failed to create invoice');
            }
        } catch (error) {
            console.error('Error creating invoice:', error);
            setErrorMessage('Error creating invoice');
        } finally {
            setIsLoading(false);
        }
    };

    const groupByCategory = (data) => {
        const grouped = {};
        if (!Array.isArray(data)) {
            return grouped;
        }
        data.forEach(item => {
            const category = item.categoryName || 'Uncategorized';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(item);
        });
        return grouped;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const getCategoryTotal = (items) => {
        return items.reduce((sum, item) => sum + (item.Amount || 0), 0);
    };

    const getTotalQuantity = () => {
        return invoiceData.reduce((sum, item) => sum + (item.Quantity || 0), 0);
    };

    const getTotalTransportation = () => {
        return invoiceData.reduce((sum, item) => sum + (item.TransportationCharges || 0), 0);
    };

    const getSubTotal = () => {
        return invoiceData.reduce((sum, item) => sum + (item.Amount || 0), 0);
    };

    const getGrandTotal = () => {
        return getSubTotal() + getTotalTransportation();
    };

    const groupedData = invoiceData.length > 0 ? groupByCategory(invoiceData) : {};

    const toggleCategory = (categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    const toggleModalCategory = (categoryName) => {
        setModalExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    return (
        <div className="invoice-generate-container">
            {/* Header */}
            <div className="invoice-header">
                <h2 className="invoice-title">
                    <span className="title-icon">📄</span>
                    Invoice Generate
                </h2>
                <div className="header-actions">
                    <Button
                        className="btn-back"
                        onClick={() => history.goBack()}
                        startIcon={<CloseIcon />}
                    >
                        Back
                    </Button>
                </div>
            </div>

            {errorMessage && (
                <div className="error-banner">
                    <span className="error-icon">⚠️</span>
                    {errorMessage}
                </div>
            )}

            {/* Existing Invoice Alert */}
            {existingInvoice && (
                <div className="invoice-exists-banner">
                    <div className="invoice-exists-content">
                        <span className="invoice-exists-icon">✓</span>
                        <div className="invoice-exists-info">
                            <h4>Invoice Already Generated</h4>
                            <p>Invoice No: <strong>{existingInvoice.BillPrefix}/{existingInvoice.BillNumber}/{existingInvoice.FinYear}</strong></p>
                            <p>Generated on: <strong>{formatDate(existingInvoice.BillDate)}</strong></p>
                        </div>
                        <Button
                            className="btn-view-details"
                            onClick={handleViewInvoiceDetails}
                            startIcon={<VisibilityIcon />}
                        >
                            View Details
                        </Button>
                    </div>
                </div>
            )}

            {/* Selection Card */}
            <div className="selection-card">
                <div className="single-line-section">
                    <div className="form-group">
                        <label>Customer Name <span className="required">*</span></label>
                        <Select
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                            options={customerOptions}
                            placeholder="Select customer..."
                            className="customer-select"
                            classNamePrefix="select"
                            isClearable
                            isSearchable={true}
                        />
                    </div>
                    <div className="form-group">
                        <label>From Date <span className="required">*</span></label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="date-input"
                        />
                    </div>
                    <div className="form-group">
                        <label>To Date <span className="required">*</span></label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="date-input"
                        />
                    </div>
                    <div className="form-group">
                        <Button
                            className="btn-load-data"
                            onClick={handleLoadData}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Loading...' : 'Load Data'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Invoice Data Table */}
            {invoiceData.length > 0 && (
                <div className="invoice-section">
                    {/* Invoice Info, Summary Totals, and Create Button in One Line */}
                    <div className="invoice-info-summary-wrapper">
                        <div className="invoice-info">
                            <h3 className="section-title">
                                📊 Invoice Details
                            </h3>
                            <p className="customer-name">
                                {selectedCustomer?.label}
                            </p>
                            <p className="date-range">
                                Period: {formatDate(fromDate)} to {formatDate(toDate)}
                            </p>
                        </div>

                        {/* Summary Totals Card */}
                        <div className="summary-totals-card">
                            <div className="summary-item">
                                <span className="summary-label">Total Quantity</span>
                                <span className="summary-value">{getTotalQuantity()}</span>
                            </div>
                            <div className="summary-item">
                                <span className="summary-label">Sub Total</span>
                                <span className="summary-value">₹ {getSubTotal().toFixed(2)}</span>
                            </div>
                            <div className="summary-item">
                                <span className="summary-label">Transportation</span>
                                <span className="summary-value">₹ {getTotalTransportation().toFixed(2)}</span>
                            </div>
                            <div className="summary-item summary-grand-total">
                                <span className="summary-label">Grand Total</span>
                                <span className="summary-value grand-total-amount">₹ {getGrandTotal().toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Create Invoice Button */}
                        <div className="create-invoice-button-inline">
                            <Button
                                className="btn-create-invoice"
                                onClick={handleCreateInvoice}
                                startIcon={<ReceiptIcon />}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating...' : 'Create Invoice'}
                            </Button>
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="invoice-table">
                            <thead>
                                <tr>
                                    <th className="col-date">Date</th>
                                    <th className="col-category">Category</th>
                                    <th className="col-item">Item Name</th>
                                    <th className="col-number">Quantity</th>
                                    <th className="col-number">Rate</th>
                                    <th className="col-number">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(groupedData).map(([categoryName, items]) => {
                                    const isExpanded = expandedCategories[categoryName] !== false;
                                    return (
                                        <>
                                            <tr
                                                key={`category-${categoryName}`}
                                                className="category-header-row"
                                                onClick={() => toggleCategory(categoryName)}
                                            >
                                                <td colSpan="6" className="category-header">
                                                    <div className="category-header-content">
                                                        <span>{categoryName}</span>
                                                        <span className="expand-icon">
                                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && items.map((item, index) => (
                                                <tr key={`${categoryName}-${index}`} className="table-row">
                                                    <td className="date-cell">{formatDate(item.RegisterDate)}</td>
                                                    <td className="category-cell">{item.CategoryName}</td>
                                                    <td className="item-cell">{item.ItemName}</td>
                                                    <td className="number-cell">{item.Quantity}</td>
                                                    <td className="number-cell">₹ {item.Rate?.toFixed(2)}</td>
                                                    <td className="number-cell amount-cell">₹ {item.Amount?.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                            <tr className="category-total-row">
                                                <td colSpan="5" className="category-total-label">
                                                    {categoryName} Total
                                                </td>
                                                <td className="category-total-value">
                                                    ₹ {getCategoryTotal(items).toFixed(2)}
                                                </td>
                                            </tr>
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!isLoading && invoiceData.length === 0 && !errorMessage && (
                <div className="empty-state">
                    <div className="empty-icon">📄</div>
                    <p>Select customer and date range, then click "Load Data" to generate invoice</p>
                </div>
            )}

            {/* Loading Overlay */}
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="advanced-spinner">
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                            <div className="spinner-icon">📄</div>
                        </div>
                        <p className="loading-text">Loading invoice data...</p>
                    </div>
                </div>
            )}

            {/* Invoice Details Modal */}
            {showInvoiceModal && invoiceDetails && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                📄 Invoice Details
                            </h3>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <CloseIcon />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="invoice-details-section">
                                <div className="invoice-info-grid">
                                    <div className="info-card">
                                        <div className="info-icon">📋</div>
                                        <div className="info-content">
                                            <span className="info-label">Invoice No</span>
                                            <span className="info-value">{invoiceDetails.BillPrefix}/{invoiceDetails.BillNumber}/{invoiceDetails.FinYear}</span>
                                        </div>
                                    </div>
                                    <div className="info-card">
                                        <div className="info-icon">📅</div>
                                        <div className="info-content">
                                            <span className="info-label">Invoice Date</span>
                                            <span className="info-value">{formatDate(invoiceDetails.BillDate)}</span>
                                        </div>
                                    </div>
                                    <div className="info-card">
                                        <div className="info-icon">👤</div>
                                        <div className="info-content">
                                            <span className="info-label">Customer</span>
                                            <span className="info-value">{invoiceDetails.CustomerName}</span>
                                        </div>
                                    </div>
                                    <div className="info-card">
                                        <div className="info-icon">📆</div>
                                        <div className="info-content">
                                            <span className="info-label">Period</span>
                                            <span className="info-value">
                                                {formatDate(invoiceDetails.InvoiceFromDate)} to {formatDate(invoiceDetails.InvoiceToDate)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-summary-grid">
                                    <div className="summary-card amount-card">
                                        <div className="summary-icon">💰</div>
                                        <div className="summary-content">
                                            <span className="summary-label">Amount</span>
                                            <span className="summary-value">₹ {invoiceDetails.Amount?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="summary-card transport-card">
                                        <div className="summary-icon">🚚</div>
                                        <div className="summary-content">
                                            <span className="summary-label">Transportation</span>
                                            <span className="summary-value">₹ {invoiceDetails.TransportationCharges?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="summary-card total-card">
                                        <div className="summary-icon">💳</div>
                                        <div className="summary-content">
                                            <span className="summary-label">Total Amount</span>
                                            <span className="summary-value total-amount">₹ {invoiceDetails.TotalAmount?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div className="modal-table-container">
                                <table className="modal-invoice-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Item</th>
                                            <th>Qty</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceChildDetails && Object.entries(groupByCategory(invoiceChildDetails)).map(([categoryName, items], catIndex) => {
                                            const isExpanded = modalExpandedCategories[categoryName] !== false;
                                            return (
                                                <>
                                                    <tr
                                                        key={`modal-category-${catIndex}`}
                                                        className="modal-category-header-row"
                                                        onClick={() => toggleModalCategory(categoryName)}
                                                    >
                                                        <td colSpan="6" className="modal-category-header">
                                                            <div className="modal-category-header-content">
                                                                <span>{categoryName}</span>
                                                                <span className="modal-expand-icon">
                                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && items.map((item, index) => (
                                                        <tr key={`modal-${catIndex}-${index}`}>
                                                            <td>{formatDate(item.RegisterDate)}</td>
                                                            <td>{item.CategoryName}</td>
                                                            <td>{item.ItemName}</td>
                                                            <td>{item.Quantity}</td>
                                                            <td>₹ {item.Rate?.toFixed(2)}</td>
                                                            <td>₹ {item.Amount?.toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                    <tr key={`modal-total-${catIndex}`} className="modal-category-total-row">
                                                        <td colSpan="5" className="modal-category-total-label">
                                                            {categoryName} Total
                                                        </td>
                                                        <td className="modal-category-total-value">
                                                            ₹ {getCategoryTotal(items).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="modal-total-row">
                                            <td colSpan="5" className="modal-total-label">Grand Total</td>
                                            <td className="modal-total-value">
                                                ₹ {invoiceDetails.TotalAmount?.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button className="btn-modal-close" onClick={closeModal}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Popup */}
            {showSuccessPopup && (
                <div className="success-popup-overlay">
                    <div className="success-popup">
                        <div className="success-icon">✓</div>
                        <h3 className="success-title">Success!</h3>
                        <p className="success-message">Invoice Created Successfully</p>
                    </div>
                </div>
            )}

            <div style={{
                position: 'fixed',
                bottom: '12px',
                right: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.3rem 0.65rem',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '14px',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
                fontFamily: 'serif',
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '0.7rem',
                backdropFilter: 'blur(10px)'
            }}>
                <CopyrightIcon />
                <b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </div>
    );
};

export default InvoiceGenerate;
